import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";

const MAX_TABLE_ROWS = 80;
const JOB_STATUS_CHUNK_SIZE = 2000;

function authConfig(token) {
  return {
    headers: {
      Authorization: "Bearer " + token,
    },
  };
}

function formatNumber(value) {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number.toLocaleString("it-IT") : "0";
}

function statusClass(status) {
  if (status === "READY") return "dark-green";
  if (status === "WARNING") return "orange";
  return "dark-red";
}

function chunkArray(values, chunkSize) {
  const chunks = [];
  for (let index = 0; index < values.length; index += chunkSize) {
    chunks.push(values.slice(index, index + chunkSize));
  }
  return chunks;
}

function csvValue(value) {
  const text =
    value === null || typeof value === "undefined" ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadTextFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function supplierOptionValue(supplier) {
  if (!supplier) return "";
  return `${supplier.supplierCode || ""}::${supplier.brandCode || ""}`;
}

function supplierOptionLabel(supplier) {
  const base = supplier.description || supplier.supplierCode;
  return supplier.brandCode ? `${base} - ${supplier.brandCode}` : `${base} - generico`;
}

function ImportMassivoImmagini({ token, onClose, embedded = false }) {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierCode, setSupplierCode] = useState("");
  const [batches, setBatches] = useState([]);
  const [batchPath, setBatchPath] = useState("");
  const [dryRun, setDryRun] = useState(null);
  const [startResult, setStartResult] = useState(null);
  const [jobStatuses, setJobStatuses] = useState({});
  const [overwriteExistingImages, setOverwriteExistingImages] = useState(false);
  const [
    deleteSourceFilesForExistingImages,
    setDeleteSourceFilesForExistingImages,
  ] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const selectedSupplier = useMemo(
    () =>
      suppliers.find(
        (supplier) => supplierOptionValue(supplier) === supplierCode,
      ),
    [suppliers, supplierCode],
  );
  const requestSupplierCode = selectedSupplier?.supplierCode || "";
  const requestBrandCode = selectedSupplier?.brandCode || null;

  const fetchSuppliers = useCallback(async () => {
    if (!token) return;
    setError("");
    try {
      const response = await axios.get(
        `${apiUrl}/moda/image-bulk-import/suppliers`,
        authConfig(token),
      );
      const data = response.data?.data ?? [];
      setSuppliers(data);
      if (!supplierCode && data.length) {
        setSupplierCode(supplierOptionValue(data[0]));
      }
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          requestError.message ||
          "Errore caricamento fornitori.",
      );
    }
  }, [token, supplierCode]);

  const fetchBatches = useCallback(async () => {
    if (!token || !requestSupplierCode) {
      setBatches([]);
      return;
    }
    setError("");
    try {
      const response = await axios.get(
        `${apiUrl}/moda/image-bulk-import/batches`,
        {
          ...authConfig(token),
          params: {
            supplierCode: requestSupplierCode,
            brandCode: requestBrandCode,
          },
        },
      );
      setBatches(response.data?.data ?? []);
    } catch (requestError) {
      setBatches([]);
      setError(
        requestError.response?.data?.error ||
          requestError.message ||
          "Errore caricamento batch.",
      );
    }
  }, [token, requestSupplierCode, requestBrandCode]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  useEffect(() => {
    setBatchPath("");
    setDryRun(null);
    setStartResult(null);
    setJobStatuses({});
    setOverwriteExistingImages(false);
    setDeleteSourceFilesForExistingImages(false);
    fetchBatches();
  }, [fetchBatches]);

  const runDryRun = async () => {
    if (!requestSupplierCode) return;
    setLoading(true);
    setError("");
    setMessage("");
    setStartResult(null);
    setJobStatuses({});
    setOverwriteExistingImages(false);
    setDeleteSourceFilesForExistingImages(false);
    try {
      const response = await axios.post(
        `${apiUrl}/moda/image-bulk-import/dry-run`,
        {
          supplierCode: requestSupplierCode,
          brandCode: requestBrandCode,
          batchPath: batchPath || null,
        },
        authConfig(token),
      );
      setDryRun(response.data?.data ?? null);
      setMessage("Analisi completata.");
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          requestError.message ||
          "Errore durante l'analisi.",
      );
    } finally {
      setLoading(false);
    }
  };

  const startImport = async () => {
    if (!requestSupplierCode) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const response = await axios.post(
        `${apiUrl}/moda/image-bulk-import/start`,
        {
          supplierCode: requestSupplierCode,
          brandCode: requestBrandCode,
          batchPath: batchPath || null,
          overwriteExistingImages,
          includeWarnings: overwriteExistingImages,
          deleteSourceFilesForExistingImages:
            deleteSourceFilesForExistingImages && !overwriteExistingImages,
        },
        authConfig(token),
      );
      const result = response.data?.data ?? null;
      setStartResult(result);
      setJobStatuses({});
      if (
        result?.existingImagesSourceCleanup &&
        !(result.scheduledJobs || []).length
      ) {
        setMessage("Pulizia sorgente completata.");
      } else if (result?.existingImagesSourceCleanup) {
        setMessage("Import avviato e pulizia sorgente completata.");
      } else {
        setMessage("Import avviato.");
      }
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          requestError.message ||
          "Errore durante l'avvio import.",
      );
    } finally {
      setLoading(false);
    }
  };

  const products = dryRun?.products ?? [];
  const tableProducts = products.slice(0, MAX_TABLE_ROWS);
  const readyProductsCount = products.filter(
    (product) => product.status === "READY",
  ).length;
  const existingImageProductsCount = products.filter(
    (product) =>
      product.hasExistingImage || Number(product.haImmagine ?? 0) === 1,
  ).length;
  const overwriteableExistingProductsCount = products.filter(
    (product) =>
      product.status === "WARNING" &&
      (product.hasExistingImage || Number(product.haImmagine ?? 0) === 1),
  ).length;
  const canCleanExistingImageSourceFiles =
    deleteSourceFilesForExistingImages &&
    !overwriteExistingImages &&
    overwriteableExistingProductsCount > 0;
  const canStart =
    dryRun &&
    (readyProductsCount > 0 ||
      (overwriteExistingImages && overwriteableExistingProductsCount > 0) ||
      canCleanExistingImageSourceFiles) &&
    !loading;
  const startButtonLabel =
    deleteSourceFilesForExistingImages &&
    !overwriteExistingImages &&
    readyProductsCount === 0
      ? "Pulisci sorgente"
      : deleteSourceFilesForExistingImages && !overwriteExistingImages
      ? "Avvia e pulisci"
      : overwriteExistingImages
      ? "Avvia e sovrascrivi"
      : "Avvia import";

  const downloadAnalysisCsv = () => {
    if (!dryRun) return;
    const header = [
      "tipo",
      "status",
      "codProd",
      "codVS",
      "brand",
      "nome",
      "haImmagine",
      "hasExistingImage",
      "numImmagini",
      "ordini",
      "file",
      "remotePath",
      "note",
      "motivo",
    ];
    const productRows = products.map((product) => [
      "prodotto",
      product.status,
      product.codProd,
      product.codVS || "",
      product.brandCode || "",
      product.nome || "",
      product.haImmagine ?? "",
      product.hasExistingImage ? 1 : 0,
      product.files?.length ?? 0,
      (product.files || []).map((file) => file.order).join(" | "),
      (product.files || []).map((file) => file.filename).join(" | "),
      (product.files || []).map((file) => file.remotePath).join(" | "),
      (product.messages || []).join(" | "),
      "",
    ]);
    const ignoredRows = (dryRun.ignoredFiles || []).map((file) => [
      "file_ignorato",
      "IGNORED",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      file.remotePath,
      "",
      file.reason,
    ]);
    const csv = [header, ...productRows, ...ignoredRows]
      .map((row) => row.map(csvValue).join(";"))
      .join("\n");
    const safeSupplier =
      dryRun.supplier?.configKey ||
      (requestBrandCode
        ? `${requestSupplierCode}-${requestBrandCode}`
        : requestSupplierCode) ||
      "fornitore";
    const safeBatch = batchPath || "cartella-principale";
    downloadTextFile(
      `analisi-import-immagini-${safeSupplier}-${safeBatch}.csv`,
      csv,
      "text/csv;charset=utf-8",
    );
  };

  const fetchJobStatuses = useCallback(async () => {
    const scheduledJobs = startResult?.scheduledJobs ?? [];
    if (!token || !scheduledJobs.length) return;

    try {
      const chunks = chunkArray(
        scheduledJobs.map((job) => job.jobId),
        JOB_STATUS_CHUNK_SIZE,
      );
      const responses = await Promise.all(
        chunks.map((jobIds) =>
          axios.post(
            `${apiUrl}/moda/image-bulk-import/jobs/status`,
            { jobIds },
            authConfig(token),
          ),
        ),
      );
      const statuses = responses.flatMap(
        (response) => response.data?.data ?? [],
      );
      setJobStatuses((current) => ({
        ...current,
        ...Object.fromEntries(statuses.map((job) => [job.jobId, job])),
      }));
    } catch (requestError) {
      setJobStatuses((current) => ({
        ...current,
        ...Object.fromEntries(
          scheduledJobs.map((job) => [
            job.jobId,
            {
              jobId: job.jobId,
              productCode: job.codVS,
              status: "ERROR",
              lastStep: "Errore lettura stato job",
              message:
                requestError.response?.data?.error ||
                requestError.message ||
                "Errore lettura stato job.",
            },
          ]),
        ),
      }));
    }
  }, [startResult, token]);

  useEffect(() => {
    const scheduledJobs = startResult?.scheduledJobs ?? [];
    if (!scheduledJobs.length) return undefined;

    fetchJobStatuses();
    const allTerminal = scheduledJobs.every((job) =>
      ["SUCCESS", "ERROR", "REJECTED"].includes(jobStatuses[job.jobId]?.status),
    );
    if (allTerminal) return undefined;

    const intervalId = setInterval(() => {
      fetchJobStatuses();
    }, 4000);

    return () => clearInterval(intervalId);
  }, [fetchJobStatuses, jobStatuses, startResult]);

  const content = (
    <div
      className={
        embedded ? "pa0" : "sfondoBiancoTema br2 shadow-4 pa4 w-90 mw8"
      }
    >
      <div className="flex items-center justify-between mb3">
        <h2 className="ma0 f3">Import massivo immagini</h2>
        {onClose && (
          <button
            className={stilePulsanti.buttonClassesWhite}
            onClick={onClose}
          >
            Chiudi
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-end nl2 nr2">
        <label className="pa2 w-100 w-30-ns">
          <span className="db ttu tracked gray mb1 f7">Fornitore / brand</span>
          <select
            className="pa2 input-reset ba b--black-20 w-100"
            value={supplierCode}
            onChange={(event) => setSupplierCode(event.target.value)}
            disabled={loading}
          >
            {!suppliers.length && <option value="">Nessun fornitore</option>}
            {suppliers.map((supplier) => (
              <option
                key={supplier.configKey || supplierOptionValue(supplier)}
                value={supplierOptionValue(supplier)}
              >
                {supplierOptionLabel(supplier)}
              </option>
            ))}
          </select>
        </label>

        <label className="pa2 w-100 w-30-ns">
          <span className="db ttu tracked gray mb1 f7">Batch</span>
          <select
            className="pa2 input-reset ba b--black-20 w-100"
            value={batchPath}
            onChange={(event) => {
              setBatchPath(event.target.value);
              setDryRun(null);
              setStartResult(null);
              setJobStatuses({});
              setOverwriteExistingImages(false);
              setDeleteSourceFilesForExistingImages(false);
            }}
            disabled={loading || !requestSupplierCode}
          >
            <option value="">Cartella principale</option>
            {batches.map((batch) => (
              <option key={batch.name} value={batch.name}>
                {batch.name}
              </option>
            ))}
          </select>
        </label>

        <div className="pa2 flex">
          <button
            className={stilePulsanti.buttonClassesWhite}
            onClick={fetchBatches}
            disabled={loading || !requestSupplierCode}
          >
            Aggiorna
          </button>
          <button
            className={stilePulsanti.buttonClassesGreen + " ml2"}
            onClick={runDryRun}
            disabled={loading || !requestSupplierCode}
          >
            Analizza
          </button>
          <button
            className={stilePulsanti.buttonClassesWhite + " ml2"}
            onClick={downloadAnalysisCsv}
            disabled={!dryRun || loading}
          >
            Scarica analisi
          </button>
          <button
            className={stilePulsanti.buttonClassesGreen + " ml2"}
            onClick={startImport}
            disabled={!canStart}
          >
            {startButtonLabel}
          </button>
        </div>
      </div>

      {selectedSupplier && (
        <div className="f6 gray mb3">
          Sorgente: import_massivo/{selectedSupplier.sourcePath}
          {selectedSupplier.brandCode ? ` (${selectedSupplier.brandCode})` : ""}
          {batchPath ? `/${batchPath}` : ""}
        </div>
      )}

      {dryRun && existingImageProductsCount > 0 && (
        <div className="pa3 mb3 ba b--orange br2 bg-washed-yellow f6 lh-copy">
          <label className="flex items-start pointer">
            <input
              type="checkbox"
              className="mt1 mr2"
              checked={overwriteExistingImages}
              onChange={(event) => {
                setOverwriteExistingImages(event.target.checked);
                if (event.target.checked) {
                  setDeleteSourceFilesForExistingImages(false);
                }
              }}
              disabled={loading}
            />
            <span>
              <span className="b">Sovrascrivi immagini esistenti</span>:{" "}
              {formatNumber(existingImageProductsCount)} articoli risultano gia
              marcati con <code>ha_immagine = 1</code>. Se non selezioni questa
              opzione, questi articoli restano esclusi dall'import; la scelta
              vale per tutto il batch.
            </span>
          </label>
          <label className="flex items-start pointer mt3">
            <input
              type="checkbox"
              className="mt1 mr2"
              checked={deleteSourceFilesForExistingImages}
              onChange={(event) =>
                setDeleteSourceFilesForExistingImages(event.target.checked)
              }
              disabled={loading || overwriteExistingImages}
            />
            <span>
              <span className="b">
                Elimina dalla sorgente i file gia coperti da immagini
              </span>
              : se non sovrascrivi, rimuove dalla cartella SFTP del batch i
              file degli articoli esclusi perche hanno gia{" "}
              <code>ha_immagine = 1</code>. Non tocca prodotti in errore o file
              fuori dalla cartella batch.
            </span>
          </label>
        </div>
      )}

      <div className="pa3 mb3 ba b--black-10 br2 bg-near-white f6 lh-copy">
        <div className="b mb2">Come preparare un import</div>
        <div className="mb2">
          Carica i file via SFTP, non dal browser, nella cartella{" "}
          <code>
            IMAGES_SFTP_BASE_PATH/import_massivo/&lt;source_path&gt;/&lt;batch&gt;
          </code>
          . Per un fornitore gia configurato usa lo stesso{" "}
          <code>source_path</code>
          dell'anno precedente e crea solo un nuovo batch, ad esempio{" "}
          <code>2027-primavera</code>.
        </div>
        <div className="mb2">
          Per un fornitore nuovo va prima creata una riga in{" "}
          <code>DAM_import_immagini_fornitori</code>: il campo{" "}
          <code>source_path</code> decide la sottocartella sotto{" "}
          <code>import_massivo</code>, mentre <code>filename_rules_json</code>{" "}
          contiene le regole per ricavare <code>MODA_Articoli.codProd</code> dal
          nome file.
        </div>
        <div className="mb2">
          Normalizzazione esempio Salewa: <code>00-0000076001_0548.jpg</code>{" "}
          diventa <code>76001-0548</code> con ordine 1;{" "}
          <code>00-0000076001_0548_2.jpg</code> diventa lo stesso{" "}
          <code>codProd</code> con ordine 2. Il primo file non deve avere{" "}
          <code>_1</code>.
        </div>
        <div>
          Usa sempre <strong>Analizza</strong> prima di avviare: il dry-run
          segnala codici non trovati, progressivi duplicati, articoli gia con
          immagine e brand mancanti. Gli articoli gia con immagine vengono
          importati solo se selezioni{" "}
          <strong>Sovrascrivi immagini esistenti</strong>; la scelta vale per
          tutto il batch.
        </div>
      </div>

      {loading && (
        <div className="pa2 colPreparazione">Operazione in corso...</div>
      )}
      {message && <div className="pa2 dark-green">{message}</div>}
      {error && <div className="pa2 dark-red">{error}</div>}

      {dryRun && (
        <div className="mt3">
          <div className="flex flex-wrap nl2 nr2">
            {[
              ["File", dryRun.summary?.totalFiles],
              ["Prodotti", dryRun.summary?.products],
              ["Validi", dryRun.summary?.validProducts],
              ["Gia con imm.", dryRun.summary?.existingImageProducts],
              ["Avvisi", dryRun.summary?.warnings],
              ["Errori", dryRun.summary?.errors],
            ].map(([label, value]) => (
              <div key={label} className="pa2 w-50 w-third-m w-20-l">
                <div className="ba b--black-10 pa3 br2">
                  <div className="f7 ttu tracked gray">{label}</div>
                  <div className="f3 fw6">{formatNumber(value)}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="overflow-auto mt3">
            <table className="w-100 collapse f6">
              <thead>
                <tr className="tl bb b--black-10">
                  <th className="pv2 pr2">Stato</th>
                  <th className="pv2 pr2">codProd</th>
                  <th className="pv2 pr2">codVS</th>
                  <th className="pv2 pr2">Brand</th>
                  <th className="pv2 pr2">Img</th>
                  <th className="pv2 pr2">Note</th>
                </tr>
              </thead>
              <tbody>
                {tableProducts.map((product) => (
                  <tr key={product.codProd} className="bb b--black-05">
                    <td
                      className={`pv2 pr2 fw6 ${statusClass(product.status)}`}
                    >
                      {product.status}
                    </td>
                    <td className="pv2 pr2">{product.codProd}</td>
                    <td className="pv2 pr2">{product.codVS || "-"}</td>
                    <td className="pv2 pr2">{product.brandCode || "-"}</td>
                    <td className="pv2 pr2">{product.files?.length ?? 0}</td>
                    <td className="pv2 pr2">
                      {(product.messages || []).join(" | ") || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {products.length > MAX_TABLE_ROWS && (
              <div className="gray f6 mt2">
                Mostrate {MAX_TABLE_ROWS} righe su {products.length}.
              </div>
            )}
          </div>
        </div>
      )}

      {startResult && (
        <div className="mt3 pa3 ba b--black-10 br2">
          <div className="fw6 mb2">Job schedulati</div>
          <div>
            {formatNumber(startResult.scheduledJobs?.length)} prodotti avviati,{" "}
            {formatNumber(startResult.skippedProducts?.length)} esclusi.
          </div>
          {startResult.existingImagesSourceCleanup && (
            <div className="mt2 f6">
              Pulizia sorgente articoli gia con immagine:{" "}
              {formatNumber(
                startResult.existingImagesSourceCleanup.removedFiles,
              )}
              /{formatNumber(startResult.existingImagesSourceCleanup.totalFiles)}{" "}
              file rimossi
              {startResult.existingImagesSourceCleanup.failedFiles > 0
                ? `, ${formatNumber(
                    startResult.existingImagesSourceCleanup.failedFiles,
                  )} errori`
                : ""}
              .
              {startResult.existingImagesSourceCleanup.error
                ? ` ${startResult.existingImagesSourceCleanup.error}`
                : ""}
            </div>
          )}
          <div className="overflow-auto mt2">
            <table className="w-100 collapse f6">
              <thead>
                <tr className="tl bb b--black-10">
                  <th className="pv1 pr2">codProd</th>
                  <th className="pv1 pr2">codVS</th>
                  <th className="pv1 pr2">Img</th>
                  <th className="pv1 pr2">Stato job</th>
                  <th className="pv1 pr2">Messaggio</th>
                  <th className="pv1 pr2">jobId</th>
                </tr>
              </thead>
              <tbody>
                {(startResult.scheduledJobs || []).slice(0, 20).map((job) => {
                  const jobStatus = jobStatuses[job.jobId];
                  return (
                    <tr key={job.jobId} className="bb b--black-05">
                      <td className="pv1 pr2">{job.codProd}</td>
                      <td className="pv1 pr2">{job.codVS}</td>
                      <td className="pv1 pr2">{job.files} immagini</td>
                      <td
                        className={`pv1 pr2 fw6 ${statusClass(
                          jobStatus?.status === "SUCCESS"
                            ? "READY"
                            : jobStatus?.status === "ERROR"
                            ? "ERROR"
                            : "WARNING",
                        )}`}
                      >
                        {jobStatus?.status || "PENDING"}
                      </td>
                      <td className="pv1 pr2">
                        {jobStatus?.message || jobStatus?.lastStep || "-"}
                      </td>
                      <td className="pv1 pr2">{job.jobId}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div
      className="fixed top-0 left-0 right-0 bottom-0 bg-black-40 z-999 flex items-start justify-center overflow-auto"
      style={{ paddingTop: 40, paddingBottom: 40 }}
    >
      {content}
    </div>
  );
}

export default ImportMassivoImmagini;
